/**
 * Het veldregister van de subsidie-beheerkant.
 *
 * Waarom dit bestand bestaat: `evaluateRule` leest de waarde van een regel met
 * `getNestedValue(data, regel.veld)` uit de intake
 * (`src/hooks/subsidie-evaluatie-core.ts:53-95`). Staat het veld niet in
 * `SubsidieIntakeData`, dan komt daar `undefined` uit en gebeurt er één van twee
 * dingen — allebei stil:
 *
 *  - `verplicht = true`  → `evaluateAllSubsidies` telt dat als datagat
 *    (`subsidie-evaluatie-core.ts:803-809`), duwt de regeling naar
 *    `gedeeltelijk` en zet er "Vul ... in om te checken" bij. Niemand kán dat
 *    invullen, want er is geen invoerveld. De regeling blijft dus voor altijd
 *    gedeeltelijk.
 *  - `verplicht = false` → de regel wordt overgeslagen en doet nooit iets.
 *
 * Daarom biedt het beheerscherm géén vrij tekstveld voor `veld` maar deze
 * keuzelijst. `IntakeVeldNaam` is `keyof SubsidieIntakeData`: hernoemt iemand
 * een veld in de intake, dan faalt de compilatie hier in plaats van dat een
 * regeling stil kapot gaat.
 *
 * Bewust NIET in de lijst: `milieulijst_letter`,
 * `kosten_flexibiliteitsscan` en `kosten_haalbaarheidsstudie`. De rekenkern
 * noemt die drie wel (`subsidie-evaluatie-core.ts:189-190, 292`), maar ze staan
 * niet in `SubsidieIntakeData` en het intakeformulier schrijft ze niet — een
 * regel erop zou precies het datagat opleveren dat hierboven beschreven staat.
 *
 * Dit bestand is met opzet vrij van React en Supabase en importeert alleen een
 * type (dat verdwijnt bij het compileren), zodat
 * `tests/subsidie/beheer.node.test.mjs` het rechtstreeks kan laden. Om die
 * reden ook een relatief pad in plaats van het `@/`-alias: de node-testrunner
 * kent dat alias niet.
 */
import type { SubsidieIntakeData } from '../../../hooks/useSubsidieIntake';

/** Elke sleutel van de intake — en niets anders. */
export type IntakeVeldNaam = keyof SubsidieIntakeData;

/**
 * Het soort waarde dat achter een veld zit. Dit bepaalt welke operatoren
 * mogen; zie `OPERATOREN_PER_VELDTYPE`.
 */
export type VeldType =
  | 'getal'
  | 'ja_nee'
  | 'keuze'
  | 'tekst'
  | 'lijst_tekst'
  | 'lijst_objecten';

/**
 * De dertien operatoren uit de CHECK-constraint `sub_regels_operator_check`.
 * Een veertiende toevoegen breekt de insert; een van deze dertien weglaten uit
 * `evaluateRule` levert `default: return false`.
 */
export type RegelOperator =
  | 'eq' | 'neq'
  | 'gt' | 'gte' | 'lt' | 'lte'
  | 'in' | 'not_in'
  | 'contains'
  | 'is_true' | 'is_false' | 'is_not_null'
  | 'between';

export interface IntakeVeld {
  veld: IntakeVeldNaam;
  label: string;
  type: VeldType;
  /** Kopje in de keuzelijst; volgt de tabbladen van `SubsidieIntakeForm`. */
  groep: string;
  /** Alleen bij `keuze` en `lijst_tekst`: de waarden die het formulier schrijft. */
  keuzes?: readonly string[];
  eenheid?: string;
}

/**
 * De 27 velden van `SubsidieIntakeData` (`src/hooks/useSubsidieIntake.ts:6-58`).
 * Alle 27 worden ook daadwerkelijk door `SubsidieIntakeForm.tsx` geschreven —
 * geteld op 8 september 2026: 27 `update('...')`-aanroepen, 27 velden in het
 * type, geen verschil. Een veld in het type dat het formulier niet schrijft zou
 * hier niet mogen staan.
 */
export const INTAKE_VELDEN: readonly IntakeVeld[] = [
  // Bedrijfsgegevens
  { veld: 'bedrijfsgrootte', label: 'Bedrijfsgrootte', type: 'keuze', groep: 'Bedrijf', keuzes: ['klein', 'middel', 'mkb', 'groot'] },
  { veld: 'fte', label: 'Aantal FTE', type: 'getal', groep: 'Bedrijf' },
  { veld: 'sector', label: 'Sector', type: 'keuze', groep: 'Bedrijf', keuzes: ['transport', 'bouw', 'retail', 'industrie', 'agrarisch', 'overheid', 'overig'] },
  { veld: 'postcode_2cijferig', label: 'Postcode (2-cijferig)', type: 'tekst', groep: 'Bedrijf' },

  // Locatie
  { veld: 'locatie_publiek_toegankelijk', label: 'Publiek toegankelijk', type: 'ja_nee', groep: 'Locatie' },
  { veld: 'locatie_type', label: 'Type locatie', type: 'keuze', groep: 'Locatie', keuzes: ['bedrijventerrein', 'nabij_weg', 'overig'] },
  { veld: 'afstand_an_weg_km', label: 'Afstand tot A/N-weg', type: 'getal', groep: 'Locatie', eenheid: 'km' },
  { veld: 'bereikbaar_6m_verhard', label: 'Bereikbaar via verharde weg >= 6 m', type: 'ja_nee', groep: 'Locatie' },
  { veld: 'toegankelijk_40u_week', label: 'Toegankelijk 40+ uur per week', type: 'ja_nee', groep: 'Locatie' },

  // Netaansluiting
  { veld: 'netaansluiting_kva', label: 'Netaansluiting', type: 'getal', groep: 'Netaansluiting', eenheid: 'kVA' },
  { veld: 'grootverbruiker', label: 'Grootverbruiker (>3x80A)', type: 'ja_nee', groep: 'Netaansluiting' },
  { veld: 'netcongestie_afname', label: 'Netcongestie (afname) in postcodegebied', type: 'ja_nee', groep: 'Netaansluiting' },
  { veld: 'netbeheerder_bevestiging_2jaar', label: 'Netbeheerder bevestigt >= 100 kVA binnen 2 jaar', type: 'ja_nee', groep: 'Netaansluiting' },

  // Laadinfra
  { veld: 'laadstations', label: 'Laadstations', type: 'lijst_objecten', groep: 'Laadinfra' },
  { veld: 'totaal_laadvermogen_kw', label: 'Totaal laadvermogen', type: 'getal', groep: 'Laadinfra', eenheid: 'kW' },
  { veld: 'doelvoertuigen', label: 'Doelvoertuigen', type: 'lijst_tekst', groep: 'Laadinfra', keuzes: ['M3', 'N2', 'N3', 'M1', 'N1'] },

  // Batterij
  { veld: 'batterij_gepland', label: 'Batterijopslag gepland', type: 'ja_nee', groep: 'Batterij' },
  { veld: 'batterij_kwh', label: 'Batterijcapaciteit', type: 'getal', groep: 'Batterij', eenheid: 'kWh' },
  { veld: 'batterij_kw', label: 'Batterijvermogen', type: 'getal', groep: 'Batterij', eenheid: 'kW' },
  { veld: 'batterij_70pct_laadinfra', label: '>= 70% van de batterij voor laadinfra', type: 'ja_nee', groep: 'Batterij' },

  // EMS / PV
  { veld: 'ems_gepland', label: 'EMS gepland', type: 'ja_nee', groep: 'EMS en PV' },
  { veld: 'pv_kwp', label: 'PV-vermogen', type: 'getal', groep: 'EMS en PV', eenheid: 'kWp' },

  // Trucks
  { veld: 'trucks_gepland', label: 'Geplande trucks', type: 'lijst_objecten', groep: 'Trucks' },

  // Investering
  { veld: 'investering_totaal', label: 'Totale investering', type: 'getal', groep: 'Investering', eenheid: 'EUR' },
  { veld: 'investering_laadinfra', label: 'Investering laadinfrastructuur', type: 'getal', groep: 'Investering', eenheid: 'EUR' },
  { veld: 'investering_batterij', label: 'Investering batterijopslag', type: 'getal', groep: 'Investering', eenheid: 'EUR' },
  { veld: 'investering_ems', label: 'Investering EMS', type: 'getal', groep: 'Investering', eenheid: 'EUR' },
];

export const VELD_GROEPEN: readonly string[] = [...new Set(INTAKE_VELDEN.map(v => v.groep))];

export function vindIntakeVeld(veld: string): IntakeVeld | null {
  return INTAKE_VELDEN.find(v => v.veld === veld) ?? null;
}

export function isBekendVeld(veld: string): boolean {
  return vindIntakeVeld(veld) !== null;
}

/**
 * Wat er gebeurt als een regel op een veld staat dat de engine niet kent.
 * Geeft `null` terug voor bekende velden, zodat de aanroeper hem direct kan
 * renderen. De twee teksten beschrijven de twee takken in
 * `evaluateAllSubsidies` (`subsidie-evaluatie-core.ts:803-816`).
 */
export function onbekendVeldUitleg(veld: string, verplicht: boolean): string | null {
  if (isBekendVeld(veld)) return null;
  if (verplicht) {
    return `De engine kent het veld "${veld}" niet: de intake heeft er geen invoer voor, dus de waarde is `
      + 'altijd leeg. Omdat deze regel verplicht is telt dat als datagat en zakt de regeling permanent naar '
      + '"gedeeltelijk" — niemand kan dit ooit invullen. Kies een veld uit de lijst of zet de regel op '
      + 'niet-verplicht.';
  }
  return `De engine kent het veld "${veld}" niet: de intake heeft er geen invoer voor. Deze regel wordt bij `
    + 'elke toetsing overgeslagen en doet dus niets. Kies een veld uit de lijst.';
}

// --- Operator bij veldtype ---------------------------------------------------

/**
 * Welke operator bij welk veldtype past. Dit is geen smaak maar een
 * afspiegeling van `evaluateRule` (`subsidie-evaluatie-core.ts:56-93`):
 *
 *  - `gt/gte/lt/lte` eisen `typeof value === 'number'`. Op een tekstveld
 *    leveren ze dus ALTIJD false. Dat is de voetangel uit `docs/onderzoek/rvo/sprila.md:172`:
 *    de daar voorgestelde regel `ocpp_versie gte '1.6'` zou nooit slagen, ook
 *    niet met een ingevulde versie, omdat een versienummer een string is. Die
 *    regel is daarom nooit ingevoerd; deze tabel maakt hem onmogelijk.
 *  - `between` eist eveneens een getal, plus een tweedelige `expected`.
 *  - `contains` werkt alleen als zowel de waarde als de verwachting een array
 *    is en gebruikt `Array.prototype.includes` — identiteitsvergelijking. Voor
 *    een lijst van objecten (`laadstations`, `trucks_gepland`) levert dat nooit
 *    een treffer, dus daar blijft alleen `is_not_null` over.
 *  - `in/not_in` vergelijken de waarde zelf met de leden van `expected`. Op een
 *    array-veld vergelijkt dat de array met de leden en is dus zinloos.
 *  - `is_true/is_false` vergelijken met `===` op een boolean.
 */
export const OPERATOREN_PER_VELDTYPE: Record<VeldType, readonly RegelOperator[]> = {
  getal: ['gte', 'gt', 'lte', 'lt', 'between', 'eq', 'neq', 'is_not_null'],
  ja_nee: ['is_true', 'is_false', 'is_not_null'],
  keuze: ['eq', 'neq', 'in', 'not_in', 'is_not_null'],
  tekst: ['eq', 'neq', 'in', 'not_in', 'is_not_null'],
  lijst_tekst: ['contains', 'is_not_null'],
  lijst_objecten: ['is_not_null'],
};

export const OPERATOR_LABELS: Record<RegelOperator, string> = {
  eq: 'is gelijk aan',
  neq: 'is niet gelijk aan',
  gt: 'is groter dan',
  gte: 'is minimaal',
  lt: 'is kleiner dan',
  lte: 'is hoogstens',
  in: 'is een van',
  not_in: 'is geen van',
  contains: 'bevat een van',
  is_true: 'is ja',
  is_false: 'is nee',
  is_not_null: 'is ingevuld',
  between: 'ligt tussen',
};

export function operatorenVoorVeld(veld: string): readonly RegelOperator[] {
  const bekend = vindIntakeVeld(veld);
  return bekend ? OPERATOREN_PER_VELDTYPE[bekend.type] : [];
}

export function operatorPastBijVeld(veld: string, operator: string): boolean {
  return operatorenVoorVeld(veld).includes(operator as RegelOperator);
}

/**
 * Waarom een operator niet bij een veld past — voor de foutmelding in het
 * scherm. Geeft `null` als hij wél past.
 */
export function operatorMismatchUitleg(veld: string, operator: string): string | null {
  const bekend = vindIntakeVeld(veld);
  if (!bekend) return null;
  if (operatorPastBijVeld(veld, operator)) return null;

  const vergelijkend = operator === 'gt' || operator === 'gte' || operator === 'lt'
    || operator === 'lte' || operator === 'between';
  if (vergelijkend) {
    return `"${OPERATOR_LABELS[operator as RegelOperator] ?? operator}" vergelijkt getallen; `
      + `"${bekend.label}" is geen getal. De engine eist typeof value === 'number' en zou deze regel `
      + 'altijd als niet-voldaan lezen.';
  }
  if (bekend.type === 'lijst_objecten') {
    return `"${bekend.label}" is een lijst van objecten. De engine kan daar alleen op controleren of hij `
      + 'gevuld is; elke inhoudelijke vergelijking valt op objectidentiteit terug en levert nooit een '
      + 'treffer.';
  }
  if (bekend.type === 'lijst_tekst') {
    return `"${bekend.label}" is een lijst. Gebruik "bevat een van" om op inhoud te toetsen; `
      + 'de andere operatoren vergelijken de hele lijst en slagen daardoor nooit.';
  }
  return `Deze operator hoort niet bij het type "${bekend.type}" van "${bekend.label}".`;
}

// --- De waarde van een regel -------------------------------------------------

/** Welke invoer de gebruiker moet leveren bij een operator. */
export type WaardeVorm = 'geen' | 'getal' | 'tekst' | 'lijst' | 'bereik';

/**
 * `sub_regels.waarde` is `jsonb NOT NULL` (gemeten op 8-9-2026 in
 * `information_schema.columns`). De drie operatoren die hun waarde niet lezen —
 * `is_true`, `is_false`, `is_not_null` — moeten dus tóch iets meekrijgen,
 * anders weigert de insert op de NOT NULL-constraint.
 *
 * De waarden hieronder zijn geen gok maar wat er vandaag in de tabel staat: de
 * vier `is_true`-regels (FLEX_E x3, SPULA x4) en de twee `is_not_null`-regels
 * (AANZET, SPRILA) dragen allemaal `true`; de ene `is_false`-regel (SPRILA)
 * draagt `false`.
 */
export const WAARDE_ZONDER_INVOER: Record<'is_true' | 'is_false' | 'is_not_null', boolean> = {
  is_true: true,
  is_false: false,
  is_not_null: true,
};

export function waardeVormVoor(veldType: VeldType, operator: RegelOperator): WaardeVorm {
  if (operator === 'is_true' || operator === 'is_false' || operator === 'is_not_null') return 'geen';
  if (operator === 'between') return 'bereik';
  if (operator === 'in' || operator === 'not_in' || operator === 'contains') return 'lijst';
  return veldType === 'getal' ? 'getal' : 'tekst';
}

export interface WaardeInvoer {
  /** Vrije invoer: één getal, één tekst, of een lijst gescheiden door komma's. */
  tekst?: string;
  /** Alleen bij `between`: de bovengrens. */
  tot?: string;
}

/**
 * De uitkomst van `bouwRegelWaarde`.
 *
 * De onderscheidende sleutel is met opzet een tekst (`soort`) en geen boolean.
 * De tsconfig van dit project staat op `strictNullChecks: false` (zie
 * CLAUDE.md, en dat blijft zo), en onder die instelling versmalt TypeScript een
 * unie met een boolean-discriminant niet: `if (r.ok) r.waarde` levert dan
 * TS2339. Met een tekst-discriminant werkt het wel.
 */
export type WaardeResultaat =
  | { soort: 'ok'; waarde: unknown }
  | { soort: 'fout'; fout: string };

type GetalResultaat =
  | { soort: 'ok'; getal: number }
  | { soort: 'fout'; fout: string };

function leesGetal(ruw: string | undefined, wat: string): GetalResultaat {
  const tekst = (ruw ?? '').trim().replace(',', '.');
  if (tekst === '') return { soort: 'fout', fout: `${wat} ontbreekt.` };
  const getal = Number(tekst);
  if (!Number.isFinite(getal)) return { soort: 'fout', fout: `${wat} is geen getal: "${ruw}".` };
  return { soort: 'ok', getal };
}

/**
 * Zet de invoer van het scherm om in de jsonb-waarde die de regel krijgt.
 *
 * Levert nooit `null` of `undefined` op bij `ok: true` — zie
 * `WAARDE_ZONDER_INVOER` voor waarom dat moet.
 */
export function bouwRegelWaarde(
  veldType: VeldType,
  operator: RegelOperator,
  invoer: WaardeInvoer = {},
): WaardeResultaat {
  const vorm = waardeVormVoor(veldType, operator);

  if (vorm === 'geen') {
    return { soort: 'ok', waarde: WAARDE_ZONDER_INVOER[operator as 'is_true' | 'is_false' | 'is_not_null'] };
  }

  if (vorm === 'getal') {
    const gelezen = leesGetal(invoer.tekst, 'De drempelwaarde');
    if (gelezen.soort === 'fout') return { soort: 'fout', fout: gelezen.fout };
    return { soort: 'ok', waarde: gelezen.getal };
  }

  if (vorm === 'bereik') {
    const van = leesGetal(invoer.tekst, 'De ondergrens');
    if (van.soort === 'fout') return { soort: 'fout', fout: van.fout };
    const tot = leesGetal(invoer.tot, 'De bovengrens');
    if (tot.soort === 'fout') return { soort: 'fout', fout: tot.fout };
    if (van.getal > tot.getal) {
      return { soort: 'fout', fout: `De ondergrens (${van.getal}) ligt boven de bovengrens (${tot.getal}).` };
    }
    return { soort: 'ok', waarde: [van.getal, tot.getal] };
  }

  if (vorm === 'lijst') {
    const delen = (invoer.tekst ?? '').split(',').map(d => d.trim()).filter(d => d !== '');
    if (delen.length === 0) return { soort: 'fout', fout: 'Vul minstens één waarde in, gescheiden door komma’s.' };
    if (veldType === 'getal') {
      const getallen: number[] = [];
      for (const deel of delen) {
        const gelezen = leesGetal(deel, 'Een van de waarden');
        if (gelezen.soort === 'fout') return { soort: 'fout', fout: gelezen.fout };
        getallen.push(gelezen.getal);
      }
      return { soort: 'ok', waarde: getallen };
    }
    return { soort: 'ok', waarde: delen };
  }

  const tekst = (invoer.tekst ?? '').trim();
  if (tekst === '') return { soort: 'fout', fout: 'De waarde ontbreekt.' };
  return { soort: 'ok', waarde: tekst };
}

// --- Volledige controle van één regel ---------------------------------------

export interface RegelConcept {
  veld: string;
  operator: string;
  verplicht: boolean;
  beschrijving: string;
  invoer?: WaardeInvoer;
}

export interface RegelControle {
  /** Blokkeert opslaan. */
  fouten: string[];
  /** Mag opgeslagen worden, maar de gebruiker hoort het te weten. */
  waarschuwingen: string[];
  /** De jsonb-waarde, alleen aanwezig als `fouten` leeg is. */
  waarde?: unknown;
}

export function controleerRegel(concept: RegelConcept): RegelControle {
  const fouten: string[] = [];
  const waarschuwingen: string[] = [];

  if (concept.beschrijving.trim() === '') {
    fouten.push('Een omschrijving is verplicht: `sub_regels.beschrijving` is NOT NULL en het dashboard toont hem bij een niet-vervulde regel.');
  }

  const veld = vindIntakeVeld(concept.veld);
  if (!veld) {
    // Naast de blokkerende fout ook de uitleg, maar alleen als er echt iets
    // ingevuld staat: bij een leeg formulier is "de engine kent het veld ''
    // niet" alleen maar ruis. Bestaande rijen in de database kunnen wél op een
    // onbekend veld staan; die moeten getoond en gerepareerd kunnen worden.
    if (concept.veld.trim() !== '') {
      const uitleg = onbekendVeldUitleg(concept.veld, concept.verplicht);
      if (uitleg) waarschuwingen.push(uitleg);
    }
    fouten.push('Kies een veld uit de lijst.');
    return { fouten, waarschuwingen };
  }

  const mismatch = operatorMismatchUitleg(concept.veld, concept.operator);
  if (mismatch) {
    fouten.push(mismatch);
    return { fouten, waarschuwingen };
  }

  const gebouwd = bouwRegelWaarde(veld.type, concept.operator as RegelOperator, concept.invoer);
  if (gebouwd.soort === 'fout') {
    fouten.push(gebouwd.fout);
    return { fouten, waarschuwingen };
  }

  if (veld.keuzes && (concept.operator === 'eq' || concept.operator === 'neq')) {
    const waarde = gebouwd.waarde;
    if (typeof waarde === 'string' && !veld.keuzes.includes(waarde)) {
      waarschuwingen.push(
        `"${waarde}" staat niet tussen de waarden die het intakeformulier voor "${veld.label}" schrijft `
        + `(${veld.keuzes.join(', ')}). De toets zal dus nooit slagen.`,
      );
    }
  }

  return { fouten, waarschuwingen, waarde: gebouwd.waarde };
}
